import { motion } from "framer-motion";
import { UserCheck, Users, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { WizardState } from "./index";

interface AuthorityStepProps {
  onNext: () => void;
  onBack: () => void;
  updateAnswer: (key: keyof WizardState, value: string | number) => void;
  updateScore: (points: number) => void;
  currentAnswer: string;
}

const options = [
  {
    id: "yes",
    label: "Yes, I am",
    description: "I make the final decisions for my business",
    icon: UserCheck,
    points: 5,
  },
  {
    id: "no",
    label: "No, I'm not",
    description: "I need to consult with others before deciding",
    icon: Users,
    points: 0,
  },
];

const AuthorityStep = ({
  onNext,
  onBack,
  updateAnswer,
  updateScore,
  currentAnswer,
}: AuthorityStepProps) => {
  const handleSelect = (optionId: string, points: number) => {
    // Remove previous score if changing answer
    if (currentAnswer) {
      const previousOption = options.find((opt) => opt.id === currentAnswer);
      if (previousOption) {
        updateScore(-previousOption.points);
      }
    }

    updateAnswer("isDecisionMaker", optionId);
    updateScore(points);
    setTimeout(onNext, 300);
  };

  return (
    <div className="space-y-8">
      <div className="text-center space-y-3">
        <h2 className="text-3xl sm:text-4xl font-bold text-foreground">
          Are you the primary decision maker?
        </h2>
        <p className="text-muted-foreground">This helps us understand your authority to invest</p>
      </div>

      <div className="grid gap-4">
        {options.map((option, index) => {
          const Icon = option.icon;
          const isSelected = currentAnswer === option.id;

          return (
            <motion.button
              key={option.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              onClick={() => handleSelect(option.id, option.points)}
              className={`
                w-full p-6 rounded-2xl text-left transition-all duration-300
                bg-white/5 backdrop-blur-sm border 
                ${
                  isSelected
                    ? "border-gold/50 shadow-[0_0_30px_hsl(var(--gold)/0.3)]"
                    : "border-white/10 hover:border-gold/30"
                }
                hover:scale-[1.02] active:scale-[0.98]
                group
              `}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="flex items-start gap-4">
                <div
                  className={`
                  w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-300
                  ${
                    isSelected
                      ? "bg-gradient-to-br from-gold/30 to-gold-light/30"
                      : "bg-white/5 group-hover:bg-white/10"
                  }
                `}
                >
                  <Icon
                    className={`w-6 h-6 transition-colors ${
                      isSelected ? "text-gold" : "text-foreground/80"
                    }`}
                  />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-foreground mb-1">
                    {option.label}
                  </h3>
                  <p className="text-sm text-muted-foreground">{option.description}</p>
                </div>
              </div>
            </motion.button>
          );
        })}
      </div>

      <div className="flex justify-start pt-4">
        <Button
          onClick={onBack}
          variant="ghost"
          className="text-muted-foreground hover:text-foreground hover:bg-white/5"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
      </div>
    </div>
  );
};

export default AuthorityStep;
